import { useMemo, useRef, type CSSProperties } from 'react';
import { prefersReducedMotion } from '@/lib/motion';
import { upcomingChange } from '@/core/analysis/segments';
import type { AnalysisResult } from '@/core/analysis/types';
import type { DisplayOptions } from '@/core/theory/display';
import type { LoopRange } from '@/audio/transport';
import { useAnimationFrame } from '@/lib/useAnimationFrame';
import { stableIndex } from '@/lib/useTimeIndex';
import { getTransport } from '@/state/playerStore';
import styles from './ChordGrid.module.css';
import { buildGrid } from './gridModel';

interface ChordGridProps {
  result: AnalysisResult;
  options: DisplayOptions;
  loop: LoopRange | null;
  textScale: number;
  barsPerLine: number;
  visualOffsetMs: number;
  onSeekBeat: (beat: number) => void;
  /** Flash the next chord this many beats ahead (F4.7); 0 = off. */
  warnBeats: number;
}

/** After the user scrolls the grid, auto-scroll waits this long (design: interactions.md). */
const USER_SCROLL_PAUSE_MS = 3000;

/** The chord grid (F4.1): bars of beats, current beat highlighted, auto-scroll, click to seek. */
export function ChordGrid({
  result,
  options,
  loop,
  textScale,
  barsPerLine,
  visualOffsetMs,
  onSeekBeat,
  warnBeats,
}: ChordGridProps) {
  const bars = useMemo(() => buildGrid(result, options), [result, options]);
  const boxRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<Element | null>(null);
  const warnRef = useRef<Element | null>(null);
  const lastBeat = useRef<number | null>(null);
  const userScrolledAt = useRef(0);

  useAnimationFrame(() => {
    const box = boxRef.current;
    const t = getTransport();
    if (!box || !t) return;
    const beat = stableIndex(
      result.beatGrid.beats,
      t.getPosition(visualOffsetMs),
      lastBeat.current ?? -1,
    );
    if (beat === lastBeat.current) return;
    lastBeat.current = beat;
    activeRef.current?.removeAttribute('data-active');
    const cell = box.querySelector<HTMLElement>(`[data-beat="${beat}"]`);
    activeRef.current = cell;
    if (!cell) return;
    cell.setAttribute('data-active', '');

    warnRef.current?.removeAttribute('data-warn');
    const upcoming = upcomingChange(result.segments, beat, warnBeats);
    warnRef.current = upcoming
      ? box.querySelector<HTMLElement>(`[data-beat="${upcoming.startBeat}"]`)
      : null;
    warnRef.current?.setAttribute('data-warn', '');

    if (Date.now() - userScrolledAt.current < USER_SCROLL_PAUSE_MS) return;
    const top = cell.offsetTop;
    if (
      top < box.scrollTop + 24 ||
      top + cell.offsetHeight > box.scrollTop + box.clientHeight - 96
    ) {
      box.scrollTo({
        top: Math.max(0, top - 96),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }
  });

  const markUserScroll = () => {
    userScrolledAt.current = Date.now();
  };

  const style = {
    '--bars-per-line': barsPerLine,
    '--beats-per-bar': result.beatGrid.beatsPerBar,
    '--cell-height': `${Math.round(50 * textScale)}px`,
    '--cell-font': `${Math.round(15 * textScale)}px`,
  } as CSSProperties;

  return (
    <section aria-label="Chord grid" className={styles.section}>
      <div className={styles.legend} aria-hidden="true">
        <span className={styles.legendItem}>
          <span className={styles.swatch} data-kind="chord" />
          Confident
        </span>
        <span className={styles.legendItem}>
          <span className={styles.swatch} data-kind="low" />
          Less certain
        </span>
        <span className={styles.legendItem}>
          <span className={styles.swatch} data-kind="no-chord" />
          N.C. = no chord
        </span>
      </div>

      <div
        ref={boxRef}
        className={styles.box}
        style={style}
        onWheel={markUserScroll}
        onTouchMove={markUserScroll}
        data-testid="chord-grid"
      >
        <div className={styles.bars}>
          {bars.map((bar) => {
            const inLoop =
              loop !== null && bar.start >= loop.start - 0.01 && bar.end <= loop.end + 0.01;
            return (
              <div key={bar.number} className={styles.barWrap}>
                <span className={styles.barNumber}>{bar.number || ''}</span>
                <div className={styles.bar} data-in-loop={inLoop || undefined}>
                  {bar.cells.map((cell, i) =>
                    cell ? (
                      <button
                        key={cell.beat}
                        type="button"
                        tabIndex={-1}
                        className={styles.cell}
                        data-beat={cell.beat}
                        data-kind={cell.kind}
                        data-low={cell.low || undefined}
                        aria-label={cell.aria}
                        onClick={() => {
                          onSeekBeat(cell.beat);
                        }}
                      >
                        {cell.label ?? <span className={styles.dot} aria-hidden="true" />}
                      </button>
                    ) : (
                      <span key={`pad-${i}`} className={styles.pad} aria-hidden="true" />
                    ),
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
